import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';

// Market destinations - countries that import agricultural products from Africa
const marketDestinations = [
  {
    id: 1,
    country: "United States",
    lat: 39.8283,
    lon: -98.5795,
    type: "market",
    imports: ["Shea Butter", "Moringa Powder", "Cocoa Products", "Cashew Nuts", "Ginger"],
    importValue: "$2.4B annually",
    description: "Major importer of African agricultural products, particularly for health food, cosmetics, and processed foods industries.",
    keyProducts: "Shea butter for cosmetics, moringa for health supplements, cocoa for chocolate industry, cashews for snacks"
  },
  {
    id: 2,
    country: "Germany",
    lat: 51.1657,
    lon: 10.4515,
    type: "market",
    imports: ["Organic Moringa", "Fair Trade Shea", "Cocoa Beans", "Sesame Seeds", "Hibiscus"],
    importValue: "$1.8B annually",
    description: "Leading European market for organic and fair-trade African agricultural products with strong demand for sustainability.",
    keyProducts: "Organic moringa leaves, certified shea butter, premium cocoa beans, sesame oil for food industry"
  },
  {
    id: 3,
    country: "United Kingdom",
    lat: 55.3781,
    lon: -3.4360,
    type: "market",
    imports: ["Shea Butter", "Moringa Products", "Cashew Nuts", "Gum Arabic", "Yams"],
    importValue: "$1.2B annually",
    description: "Strong market for natural and organic African products, especially in health and beauty sectors.",
    keyProducts: "Raw shea butter, moringa powder, cashew nuts, gum arabic for food industry"
  },
  {
    id: 4,
    country: "France",
    lat: 46.2276,
    lon: 2.2137,
    type: "market",
    imports: ["Premium Shea", "Moringa Oil", "Specialty Corn"],
    importValue: "$1.5B annually",
    description: "High-end market for premium African agricultural products, particularly for luxury cosmetics and gourmet foods.",
    keyProducts: "Refined shea butter, cold-pressed moringa oil, specialty corn varieties"
  },
  {
    id: 5,
    country: "Japan",
    lat: 36.2048,
    lon: 138.2529,
    type: "market",
    imports: ["Moringa Supplements", "Shea Cosmetics", "Corn Products"],
    importValue: "$900M annually",
    description: "Growing market for African superfoods and natural cosmetic ingredients with focus on quality and purity.",
    keyProducts: "Moringa powder for supplements, shea butter for skincare, corn for food manufacturing"
  },
  {
    id: 6,
    country: "Canada",
    lat: 56.1304,
    lon: -106.3468,
    type: "market",
    imports: ["Organic Moringa", "Natural Shea", "Corn Meal"],
    importValue: "$600M annually",
    description: "Significant importer of natural and organic African agricultural products for health and wellness markets.",
    keyProducts: "Certified organic moringa, unrefined shea butter, stone-ground corn meal"
  },
  {
    id: 7,
    country: "Netherlands",
    lat: 52.1326,
    lon: 5.2913,
    type: "market",
    imports: ["Fair Trade Products", "Bulk Shea", "Corn Derivatives"],
    importValue: "$800M annually",
    description: "Major European distribution hub for African agricultural products with strong focus on ethical sourcing.",
    keyProducts: "Fair trade certified products, bulk shea nuts, corn-based animal feed"
  },
  {
    id: 8,
    country: "Australia",
    lat: -25.2744,
    lon: 133.7751,
    type: "market",
    imports: ["Health Superfoods", "Natural Cosmetics", "Specialty Grains"],
    importValue: "$400M annually",
    description: "Growing market for African superfoods and natural products with increasing demand for health-conscious consumers.",
    keyProducts: "Moringa superfood products, natural shea butter, premium corn varieties"
  }
];

// Source locations for our agricultural products across Africa
const sourceLocations = [
  // West Africa - Nigeria
  {
    id: 1,
    country: "Nigeria",
    region: "Northern Nigeria",
    lat: 10.5222,
    lon: 7.4383,
    type: "source",
    sourceName: "Northern Agricultural Belt",
    description: "Primary source region for grains, legumes and root crops with ideal growing conditions and established farming communities.",
    products: ["Millet", "Sorghum", "Groundnuts", "Ginger", "Yams"],
    farmingType: "Smallholder & Commercial",
    harvestSeason: "October - December",
    keyAdvantage: "Optimal climate for grain and spice cultivation"
  },
  {
    id: 2,
    country: "Nigeria",
    region: "Middle Belt",
    lat: 8.9670,
    lon: 4.5583,
    type: "source",
    sourceName: "Shea & Moringa Belt",
    description: "Traditional shea belt region with abundant wild shea trees and established communities skilled in shea nut harvesting and moringa cultivation.",
    products: ["Shea Nuts", "Shea Butter", "Moringa Leaves", "Moringa Oil", "Gum Arabic"],
    farmingType: "Wild Harvesting & Organic Farming",
    harvestSeason: "May - August (Shea), Year-round (Moringa)",
    keyAdvantage: "High-quality shea nuts and nutrient-dense moringa"
  },
  {
    id: 3,
    country: "Nigeria",
    region: "Southern Nigeria",
    lat: 5.9631,
    lon: 8.3270,
    type: "source",
    sourceName: "Tropical Crop Zone",
    description: "Rainforest region ideal for cocoa, cassava, and tropical fruit production with year-round favorable climate.",
    products: ["Cocoa Beans", "Cassava", "Plantains", "Yams", "Palm Oil"],
    farmingType: "Smallholder Farming",
    harvestSeason: "October - January (Main), May - August (Light)",
    keyAdvantage: "Premium quality cocoa and high-yield cassava varieties"
  },

  // West Africa - Ghana
  {
    id: 4,
    country: "Ghana",
    region: "Ashanti Region",
    lat: 6.6885,
    lon: -1.6244,
    type: "source",
    sourceName: "Golden Cocoa Triangle",
    description: "World's second-largest cocoa producing region known for premium quality beans with distinctive flavor profiles.",
    products: ["Premium Cocoa Beans", "Cocoa Butter", "Shea Butter", "Cashew Nuts"],
    farmingType: "Smallholder & Cooperative",
    harvestSeason: "October - March (Main), May - September (Light)",
    keyAdvantage: "Award-winning cocoa quality and Fair Trade certification"
  },

  // West Africa - Côte d'Ivoire
  {
    id: 5,
    country: "Côte d'Ivoire",
    region: "Central-West",
    lat: 6.8277,
    lon: -5.2893,
    type: "source",
    sourceName: "World's Cocoa Capital",
    description: "Leading global cocoa producer with extensive plantations and modern processing facilities.",
    products: ["Cocoa Beans", "Cocoa Products", "Cashew Nuts", "Cotton"],
    farmingType: "Commercial Plantations",
    harvestSeason: "October - March",
    keyAdvantage: "Largest volume producer with consistent quality"
  },

  // West Africa - Mali
  {
    id: 6,
    country: "Mali",
    region: "Southern Mali",
    lat: 12.6392,
    lon: -8.0029,
    type: "source",
    sourceName: "Sahel Cotton & Grains",
    description: "Major cotton and millet producing region with traditional farming practices and irrigation systems.",
    products: ["Cotton", "Millet", "Sorghum", "Groundnuts", "Shea Nuts"],
    farmingType: "Cooperative Farming",
    harvestSeason: "November - February",
    keyAdvantage: "High-quality cotton fiber and drought-resistant grains"
  },

  // West Africa - Burkina Faso
  {
    id: 7,
    country: "Burkina Faso",
    region: "Central Plateau",
    lat: 12.2383,
    lon: -1.5616,
    type: "source",
    sourceName: "Shea Capital of Africa",
    description: "Leading shea nut producer with established women's cooperatives and traditional processing methods.",
    products: ["Shea Nuts", "Shea Butter", "Sesame Seeds", "Cotton"],
    farmingType: "Women's Cooperatives",
    harvestSeason: "May - September",
    keyAdvantage: "Premium shea butter quality and Fair Trade practices"
  },

  // East Africa - Kenya
  {
    id: 8,
    country: "Kenya",
    region: "Central Kenya",
    lat: -0.0236,
    lon: 37.9062,
    type: "source",
    sourceName: "Highland Coffee & Tea",
    description: "High-altitude region producing premium coffee and tea with volcanic soil and ideal climate conditions.",
    products: ["Coffee Beans", "Tea", "Macadamia Nuts", "Avocados", "French Beans"],
    farmingType: "Estate & Smallholder",
    harvestSeason: "October - December (Main), April - June (Fly)",
    keyAdvantage: "Premium altitude coffee and organic certifications"
  },

  // East Africa - Uganda
  {
    id: 9,
    country: "Uganda",
    region: "Central Uganda",
    lat: 1.3733,
    lon: 32.2903,
    type: "source",
    sourceName: "Pearl of Africa Highlands",
    description: "Fertile region known for coffee, vanilla, and tropical fruits with year-round favorable climate.",
    products: ["Coffee Beans", "Vanilla", "Bananas", "Plantains", "Sesame Seeds"],
    farmingType: "Smallholder Farming",
    harvestSeason: "March - May, September - December",
    keyAdvantage: "Premium vanilla quality and diverse crop production"
  },

  // East Africa - Ethiopia
  {
    id: 10,
    country: "Ethiopia",
    region: "Ethiopian Highlands",
    lat: 9.1450,
    lon: 40.4897,
    type: "source",
    sourceName: "Birthplace of Coffee",
    description: "Original coffee growing region with ancient varieties and traditional processing methods.",
    products: ["Arabica Coffee", "Sesame Seeds", "Teff", "Berbere Spices"],
    farmingType: "Traditional & Organic",
    harvestSeason: "October - February",
    keyAdvantage: "Heirloom coffee varieties and ancient grains"
  },

  // Southern Africa - South Africa
  {
    id: 11,
    country: "South Africa",
    region: "Western Cape",
    lat: -33.9249,
    lon: 18.4241,
    type: "source",
    sourceName: "Mediterranean Climate Zone",
    description: "Premium fruit and wine producing region with Mediterranean climate and modern agricultural practices.",
    products: ["Citrus Fruits", "Grapes", "Apples", "Pears", "Nuts"],
    farmingType: "Commercial Farming",
    harvestSeason: "February - August",
    keyAdvantage: "Export-grade fruit quality and Global GAP certification"
  },

  // Central Africa - Cameroon
  {
    id: 12,
    country: "Cameroon",
    region: "South-West Region",
    lat: 4.0511,
    lon: 9.7679,
    type: "source",
    sourceName: "Volcanic Soil Highlands",
    description: "Volcanic soil region ideal for cocoa, coffee, and spice production with rich mineral content.",
    products: ["Cocoa Beans", "Coffee", "Black Pepper", "White Pepper", "Plantains"],
    farmingType: "Plantation & Smallholder",
    harvestSeason: "September - March",
    keyAdvantage: "Volcanic soil enrichment and premium spice quality"
  }
];

// D3 Globe Component
const D3Globe = ({ onMarkerClick }) => {
  const svgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 400, height: 400 });

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll("*").remove();

    const width = dimensions.width;
    const height = dimensions.height;
    const sensitivity = 75;

    // Create projection
    const projection = d3.geoOrthographic()
      .scale(180)
      .center([0, 0])
      .rotate([0, -30])
      .translate([width / 2, height / 2])
      .clipAngle(90);

    const initialScale = projection.scale();
    let path = d3.geoPath().projection(projection);

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    // Add invisible background for drag events
    svg.append("rect")
      .attr("width", width)
      .attr("height", height)
      .style("fill", "transparent")
      .style("cursor", "grab");

    // Create globe background (sphere)
    svg.append("defs").append("path")
      .datum({type: "Sphere"})
      .attr("id", "sphere")
      .attr("d", path);

    svg.append("use")
      .attr("class", "sphere-stroke")
      .attr("xlink:href", "#sphere")
      .style("fill", "none")
      .style("stroke", "#B8941E")
      .style("stroke-width", "2px");

    svg.append("use")
      .attr("class", "sphere-fill")
      .attr("xlink:href", "#sphere")
      .style("fill", "#D4AF37");

    // Create graticule (grid lines)
    const graticule = d3.geoGraticule()
      .step([10, 10]);

    svg.append("path")
      .datum(graticule)
      .attr("class", "graticule")
      .attr("d", path)
      .style("fill", "none")
      .style("stroke", "#B8941E")
      .style("stroke-width", "0.5px")
      .style("stroke-opacity", "0.4");

    // MOVE DRAG BEHAVIOR AND AUTO-ROTATION TO COMPONENT LEVEL
    // Drag behavior and auto-rotation control (moved outside of data loading)
    let isDragging = false;
    let autoRotationTimer = null;
    let rotation = 0; // Y-axis rotation (longitude)
    const fixedXRotation = -30; // Fixed X-axis rotation (latitude)
    let markersGroup = svg.append("g"); // Create markers group early

    // Function to update all globe elements
    const updateGlobe = () => {
      path = d3.geoPath().projection(projection);
      svg.selectAll("path").attr("d", path);
      svg.select("#sphere").attr("d", path);
      
      // Update markers
      markersGroup.selectAll("g")
        .attr("transform", d => {
          const coords = projection([d.lon, d.lat]);
          return coords ? `translate(${coords[0]}, ${coords[1]})` : "translate(-9999, -9999)";
        })
        .style("display", d => {
          const angle = d3.geoDistance([d.lon, d.lat], [-projection.rotate()[0], -projection.rotate()[1]]);
          return angle < Math.PI / 2 ? "block" : "none";
        });
    };

    // Auto-rotation function
    const startAutoRotation = () => {
      if (autoRotationTimer) {
        autoRotationTimer.stop();
      }
      
      const rotate = () => {
        if (!isDragging) {
          rotation += 0.2;
          projection.rotate([rotation, fixedXRotation]);
          updateGlobe();
        }
      };

      autoRotationTimer = d3.timer(rotate);
    };

    // Drag behavior
    const drag = d3.drag()
      .on("start", () => {
        isDragging = true;
        svg.style("cursor", "grabbing");
        if (autoRotationTimer) {
          autoRotationTimer.stop();
        }
      })
      .on("drag", (event) => {
        const currentRotate = projection.rotate();
        const k = sensitivity / projection.scale();
        // Only allow Y-axis rotation (longitude) - ignore event.dy for X-axis
        const newYRotation = currentRotate[0] + event.dx * k;
        projection.rotate([newYRotation, fixedXRotation]);
        
        // Update the rotation variable to sync with manual dragging
        rotation = newYRotation;
        updateGlobe();
      })
      .on("end", () => {
        isDragging = false;
        svg.style("cursor", "grab");
        // Auto-rotation will continue from current rotation position
        setTimeout(() => {
          if (!isDragging) {
            startAutoRotation();
          }
        }, 2000);
      });

    // Apply drag to the entire SVG immediately
    svg.call(drag);

    // Start auto-rotation initially
    startAutoRotation();

    // Load world data and render countries
    d3.json("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json").then(world => {
      // Add land masses
      svg.insert("path", ".graticule")
        .datum(topojson.feature(world, world.objects.land))
        .attr("class", "land")
        .attr("d", path)
        .style("fill", "#008000");

      // Add country boundaries
      svg.insert("path", ".graticule")
        .datum(topojson.mesh(world, world.objects.countries, (a, b) => a !== b))
        .attr("class", "boundaries")
        .attr("d", path)
        .style("fill", "none")
        .style("stroke", "#006600")
        .style("stroke-width", "0.5px");

      // Combine all markers data (both sources and markets)
      const allMarkers = [...sourceLocations, ...marketDestinations];

      // Add all markers (using the pre-created markersGroup)
      const markers = markersGroup.selectAll("g")
        .data(allMarkers)
        .enter()
        .append("g")
        .attr("transform", d => {
          const coords = projection([d.lon, d.lat]);
          return coords ? `translate(${coords[0]}, ${coords[1]})` : "translate(-9999, -9999)";
        })
        .style("cursor", "pointer")
        .style("display", d => {
          const angle = d3.geoDistance([d.lon, d.lat], [-projection.rotate()[0], -projection.rotate()[1]]);
          return angle < Math.PI / 2 ? "block" : "none";
        })
        .style("pointer-events", "all")
        .on("click", (event, d) => {
          event.stopPropagation();
          event.preventDefault();
          onMarkerClick(d);
        })
        .on("mousedown", (event) => {
          event.stopPropagation();
        });

      // Add marker circles with different colors for source vs market
      markers.append("circle")
        .attr("r", 5)
        .attr("fill", d => d.type === "source" ? "#D4AF37" : "#33CC33")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);

      // Add pulsing effect with different colors
      markers.append("circle")
        .attr("r", 5)
        .attr("fill", "none")
        .attr("stroke", d => d.type === "source" ? "#D4AF37" : "#33CC33")
        .attr("stroke-width", 2)
        .attr("opacity", 0.5)
        .attr("class", "pulse");
    }).catch(error => {
      console.error("Error loading world data:", error);
      
      // Fallback: create a simple sphere with dots if world data fails to load
      const dots = [];
      const dotCount = 1000;
      
      for (let i = 0; i < dotCount; i++) {
        const u = Math.random();
        const v = Math.random();
        
        const lat = Math.acos(2 * v - 1) * 180 / Math.PI - 90;
        const lon = 2 * Math.PI * u * 180 / Math.PI - 180;
        
        dots.push([lon, lat]);
      }

      const dotsGroup = svg.append("g");
      
      dotsGroup.selectAll("circle")
        .data(dots)
        .enter()
        .append("circle")
        .attr("cx", d => projection(d)[0])
        .attr("cy", d => projection(d)[1])
        .attr("r", 0.8)
        .attr("fill", "#3b82f6")
        .attr("fill-opacity", 0.6);

      // Add all markers for fallback (both sources and markets)
      const fallbackMarkersGroup = svg.append("g");
      const allMarkersFallback = [...sourceLocations, ...marketDestinations];
      
      const markers = fallbackMarkersGroup.selectAll("g")
        .data(allMarkersFallback)
        .enter()
        .append("g")
        .attr("transform", d => {
          const coords = projection([d.lon, d.lat]);
          return coords ? `translate(${coords[0]}, ${coords[1]})` : "translate(-9999, -9999)";
        })
        .style("cursor", "pointer")
        .on("click", (event, d) => {
          event.stopPropagation();
          onMarkerClick(d);
        });

      markers.append("circle")
        .attr("r", 5)
        .attr("fill", d => d.type === "source" ? "#D4AF37" : "#33CC33")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);

      markers.append("circle")
        .attr("r", 5)
        .attr("fill", "none")
        .attr("stroke", d => d.type === "source" ? "#D4AF37" : "#33CC33")
        .attr("stroke-width", 2)
        .attr("opacity", 0.5)
        .attr("class", "pulse");
    });

    // Cleanup function
    return () => {
      if (autoRotationTimer) {
        autoRotationTimer.stop();
      }
    };

  }, [dimensions, onMarkerClick]);

  return (
    <div className="relative overflow-visible">
      <svg ref={svgRef} className="w-full h-full cursor-grab active:cursor-grabbing overflow-visible" />
      <style>{`
        @keyframes pulse {
          0% {
            r: 5;
            opacity: 0.5;
          }
          100% {
            r: 15;
            opacity: 0;
          }
        }
        .pulse {
          animation: pulse 2s ease-out infinite;
        }
      `}</style>
    </div>
  );
};

// Main Interactive Globe Component
const InteractiveGlobe = () => {
  const [selectedStory, setSelectedStory] = useState(null);
  
  const handleMarkerClick = (market) => {
    setSelectedStory(market);
  };

  const closeModal = () => {
    setSelectedStory(null);
  };

  return (
    <section className="py-20 sm:py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="text-center mb-16"
        >
                      <h2 className="heading-lg text-text-primary mb-6">
            Our African Sourcing Network
          </h2>
                      <p className="body-lg text-text-secondary max-w-3xl mx-auto">
            Discover our extensive sourcing network across 12 African countries and the global markets hungry for premium African agricultural products. Blue markers show our sourcing locations, green markers reveal export opportunities worldwide.
          </p>
        </motion.div>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 lg:items-center">
          {/* 3D Globe */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative interactive-globe-container order-2 lg:order-1 py-8 flex flex-col items-center"
          >
            <div className="w-[280px] h-[280px] sm:w-[350px] sm:h-[350px] lg:w-[400px] lg:h-[400px] overflow-visible mx-auto flex items-center justify-center">
              <D3Globe onMarkerClick={handleMarkerClick} />
            </div>

            {/* Instructions */}
            <div className="mt-6 text-center w-full">
              <p className="text-xs sm:text-sm text-text-secondary px-4">
                <Icon name="MousePointer" size={16} className="inline mr-2" />
                <span className="hidden sm:inline">We deliver premium African agricultural products from across the continent to global markets</span>
                <span className="sm:hidden">Tap markers for locations</span>
              </p>
            </div>
          </motion.div>

          {/* Success Metrics */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-6 lg:space-y-8 order-1 lg:order-2"
          >
            <div className="glass-card p-6 lg:p-8">
              <h3 className="text-xl lg:text-2xl font-bold text-primary mb-4 lg:mb-6">Source Overview</h3>
              <div className="grid grid-cols-2 gap-4 lg:gap-6">
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-accent mb-1 lg:mb-2">12</div>
                  <div className="text-xs lg:text-sm text-text-secondary">Source Regions</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-accent mb-1 lg:mb-2">50+</div>
                  <div className="text-xs lg:text-sm text-text-secondary">Product Types</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-accent mb-1 lg:mb-2">12</div>
                  <div className="text-xs lg:text-sm text-text-secondary">African Countries</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl lg:text-3xl font-bold text-accent mb-1 lg:mb-2">Premium</div>
                  <div className="text-xs lg:text-sm text-text-secondary">Quality Focus</div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 lg:p-8">
              <h4 className="text-lg lg:text-xl font-semibold text-primary mb-3 lg:mb-4">Source Regions</h4>
              <div className="space-y-3 lg:space-y-4">
                {(() => {
                  // Select diverse regions from different countries for display
                  const diverseRegions = [
                    sourceLocations.find(loc => loc.country === "Nigeria" && loc.id === 2), // Shea & Moringa Belt
                    sourceLocations.find(loc => loc.country === "Ghana"), // Golden Cocoa Triangle
                    sourceLocations.find(loc => loc.country === "Kenya") // Highland Coffee & Tea
                  ].filter(Boolean);
                  
                  return diverseRegions.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => handleMarkerClick(location)}
                      className="w-full flex items-center space-x-3 lg:space-x-4 p-3 bg-surface-100 rounded-lg hover:bg-surface-200 transition-colors duration-200 text-left"
                    >
                      <div className="w-3 h-3 bg-accent rounded-full animate-pulse flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-text-primary text-sm lg:text-base truncate">{location.sourceName}</div>
                        <div className="text-xs lg:text-sm text-text-secondary">{location.region}, {location.country}</div>
                      </div>
                      <Icon name="ChevronRight" size={16} className="text-text-secondary flex-shrink-0" />
                    </button>
                  ));
                })()}
              </div>
            </div>

          </motion.div>
        </div>
      </div>

      {/* Source Location Modal */}
      <AnimatePresence>
        {selectedStory && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-secondary-900/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-4 sm:p-6 lg:p-8 max-h-[90vh] overflow-y-auto border border-border"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                  <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedStory.type === "source" ? selectedStory.sourceName : selectedStory.country}
                  </h3>
                  <p className="text-gray-600">
                    {selectedStory.type === "source" 
                      ? `${selectedStory.region}, ${selectedStory.country}` 
                      : `Market Destination`}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-250"
                >
                  <Icon name="X" size={24} className="text-gray-500" />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={selectedStory.type === "source" ? "MapPin" : "Globe"} 
                    size={24} 
                    className={`${selectedStory.type === "source" ? "text-sky-500" : "text-emerald-500"} mt-1 flex-shrink-0`} 
                  />
                  <p className="text-gray-800 text-lg leading-relaxed">
                    {selectedStory.description}
                  </p>
                </div>
              </div>

              {selectedStory.type === "source" ? (
                <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-lg font-bold text-accent mb-1">{selectedStory.farmingType}</div>
                      <div className="text-sm text-gray-600">Farming Type</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-lg font-bold text-success mb-1">{selectedStory.harvestSeason}</div>
                      <div className="text-sm text-gray-600">Harvest Season</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-lg font-bold text-primary mb-1">Active</div>
                      <div className="text-sm text-gray-600">Source Status</div>
                </div>
              </div>

              <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Products from this Region:</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedStory.products.map((product, index) => (
                    <span
                      key={index}
                          className="px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium"
                        >
                          {product}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Advantage:</h4>
                    <div className="p-4 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                      <p className="text-gray-800 font-medium">{selectedStory.keyAdvantage}</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-lg font-bold text-emerald-600 mb-1">{selectedStory.importValue}</div>
                      <div className="text-sm text-gray-600">Import Value</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-lg font-bold text-primary mb-1">High Demand</div>
                      <div className="text-sm text-gray-600">Market Status</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">What {selectedStory.country} Imports:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedStory.imports.map((product, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Import Categories:</h4>
                    <div className="p-4 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
                      <p className="text-gray-800 font-medium">{selectedStory.keyProducts}</p>
                    </div>
                  </div>
                </>
              )}

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => {
                    closeModal();
                    const element = document.getElementById('contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="flex-1 btn-primary text-sm sm:text-base"
                >
                  {selectedStory.type === "source" ? "Learn More About Our Sources" : "Explore Export Opportunities"}
                </button>
                <button
                  onClick={closeModal}
                  className="btn-secondary text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default InteractiveGlobe;